import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Call, CallInfo } from 'src/types/call'
import { removeNoteByIndex } from 'src/utils/notes'
import type { AppThunk } from '../store'
import { HTTP } from '../lib/axios'
import { LoadingPayload, PhoneLoadingState } from '../types/common'
import { User } from '../types/user'
import { checkValidNumber, sortListByDate } from '../utils/common'
import { Iuser, Iconference } from '../types/twilio'
import { KazooLiveNote, LiveNote } from '../types/communication'
import wait from '../utils/wait'

export interface PhoneState {
  loading: PhoneLoadingState
  selectedCall: string
  selectedNote: string
  selectedNotes: Array<string>
  users: any
  kazooUsers: any
  usersExtenstionMap: any
  australisUsers: any
  conferences: Array<Iconference>
  liveNotes: {
    [key: string]: LiveNote
  }
  callHistory: Array<Call>
  callInfo: Array<CallInfo>
  settings: any
}

const mockLiveNotes = {
  '1234': {
    interactionId: '1234',
    channelEvent: 'channel_create',
    direction: 'outbound',
    callId: '1234',
    conf_status: 'inProgress',
    dateCreated: '1678741892045',
    startTime: 1678741892045,
    contactIdNumber: '16042249376',
    subject: 'Test 2',
    note: '',
    matterId: '',
    sender: [
      {
        id: '583879ab-d55d-4786-8d7b-8f9ae29ffda5',
        type: 'User',
        phoneNumber: '16043431926',
        displayName: 'Eric Admin'
      }
    ],
    receiver: [
      {
        id: '86e0c71d-b720-45a4-86e2-e8ff0684bd36',
        type: 'Contact',
        phoneNumber: '16042249376',
        displayName: 'BC Hydro'
      }
    ]
  },
  '5678': {
    interactionId: '5678',
    channelEvent: 'channel_create',
    callId: '5678',
    conf_status: 'inProgress',
    dateCreated: '1678741866156',
    startTime: 1679096479269,
    direction: 'inbound',
    contactIdNumber: '12012479508',
    subject: 'Test 1',
    note: '',
    matterId: '',
    receiver: [
      {
        id: '583879ab-d55d-4786-8d7b-8f9ae29ffda5',
        type: 'User',
        phoneNumber: '16043431926',
        displayName: 'Eric Admin'
      }
    ],
    sender: [
      {
        id: '1fc9149a-f3b3-4f28-a28c-0b2241e2d88d',
        type: 'Contact',
        phoneNumber: '12012479508',
        displayName: 'John Kemenczy'
      }
    ]
  },
  '9101': {
    interactionId: '9101',
    channelEvent: 'channel_create',
    callId: '9101',
    conf_status: 'inProgress',
    dateCreated: '1678741905966',
    startTime: 1678741905966,
    direction: 'inbound',
    contactIdNumber: '19097090356',
    subject: 'Test 3',
    note: '',
    matterId: '',
    receiver: [
      {
        id: '583879ab-d55d-4786-8d7b-8f9ae29ffda5',
        type: 'User',
        phoneNumber: '16043431926',
        displayName: 'Eric Admin'
      }
    ],
    sender: [
      {
        id: 'e642c3aa-680c-4897-af6b-04947d8f9a03',
        type: 'Contact',
        phoneNumber: '19097090356',
        displayName: 'Ramy Abouelsood'
      }
    ]
  }
} as any

export const initialState: PhoneState = {
  loading: {
    account: false,
    notes: false,
    history: false,
    settings: false
  },
  users: {},
  kazooUsers: {},
  usersExtenstionMap: {},
  liveNotes: {},
  australisUsers: [],
  conferences: [],
  selectedCall: '',
  selectedNote: '',
  selectedNotes: [],
  callHistory: [],
  callInfo: [],
  settings: {
    defaultNoteSlide: true
  }
}

const phoneSlice = createSlice({
  name: 'phone',
  initialState,
  reducers: {
    setLoading: (state, { payload }: PayloadAction<LoadingPayload>) => {
      const clone = { ...state.loading, [payload.type]: payload.payload }
      state.loading = clone
    },
    setSelectedCall: (state, { payload }: PayloadAction<string>) => {
      state.selectedCall = payload
    },
    setSelectedNote: (state, { payload }: PayloadAction<string>) => {
      state.selectedNote = payload
    },
    setCallHistory: (state, { payload }: PayloadAction<any>) => {
      state.callHistory = payload
    },
    setCallInfo: (state, { payload }) => {
      state.callInfo = payload
    },
    updateConferences: (state, { payload }: PayloadAction<Iconference>) => {
      const existingIndex = state.conferences.findIndex(
        (conf) => conf.conferenceSid === payload.conferenceSid
      )
      if (existingIndex > -1) {
        const clone = [...state.conferences]
        clone[existingIndex] = payload
        state.conferences = clone
      } else {
        const clone = [...state.conferences]
        clone.push({
          ...payload,
          dateCreated:
            typeof payload.dateCreated === 'string'
              ? parseInt(payload.dateCreated, 10)
              : payload.dateCreated
        })
        state.conferences = sortListByDate(clone, 'dateCreated', true)
      }
    },
    updateUsers: (state, { payload }: PayloadAction<Iuser>) => {
      if (state.users[payload.australisId]) {
        const clone = { ...state.users }
        clone[payload.australisId] = {
          ...clone[payload.australisId],
          ...payload
        }
        state.users = clone
      } else {
        state.users = { ...state.users, [payload.australisId]: payload }
      }
    },
    setConferences: (state, { payload }: PayloadAction<Array<Iconference>>) => {
      const formatted = sortListByDate(
        payload.map((conf) => {
          if (typeof conf.dateCreated === 'string') {
            return { ...conf, dateCreated: parseInt(conf.dateCreated, 10) }
          }
          return conf
        }),
        'dateCreated',
        true
      )
      state.conferences = formatted
    },
    setUsers: (state, { payload }: PayloadAction<{ australisId: User }>) => {
      const uMap: any = {}
      for (const [key, value] of Object.entries(payload)) {
        const ext = value?.extension
        if (ext && !uMap[ext]) {
          uMap[ext] = value
        }
      }
      state.usersExtenstionMap = uMap
      state.users = payload
    },
    setKazooUsers: (state, { payload }: PayloadAction<any>) => {
      const fullUsers = {
        account: payload.kazooAccountId
      }
      const users = payload.kazooUsers
      Object.entries(users).forEach(([key, value]) => {
        const id = key.replaceAll('-', '')
        fullUsers[id] = value
      })
      state.users = fullUsers
    },
    setAustralisUsers: (state, { payload }: PayloadAction<any>) => {
      state.australisUsers = payload
    },
    removeConference: (state, { payload }: PayloadAction<string>) => {
      const newConfs = state.conferences.filter((conf) => {
        return conf.conferenceSid !== payload
      })
      state.conferences = newConfs
    },
    setSelectedNotes: (state, { payload }: PayloadAction<string>) => {
      if (!state.selectedNotes.includes(payload)) {
        const clone = [...state.selectedNotes, payload]
        state.selectedNotes = clone
      }
    },
    removeSelectedNotes: (state, { payload }: PayloadAction<string>) => {
      state.selectedNotes = removeNoteByIndex(state.selectedNotes, payload)
    },
    removeKazooLiveNote: (state, { payload }: PayloadAction<string>) => {
      const clone = { ...state.liveNotes }
      delete clone[payload]
      state.liveNotes = clone
      state.selectedNotes = removeNoteByIndex(state.selectedNotes, payload)
    },
    updateKazooLiveNotes: (state, { payload }: PayloadAction<any>) => {
      const { channelEvent, interactionId } = payload

      if (!interactionId) return

      const isDestroy = channelEvent === 'channel_destroy'
      const isNoteSelected = state.selectedNotes.includes(interactionId)

      if (isDestroy) {
        const clone = { ...state.liveNotes }
        if (!clone[interactionId]) return

        if (state.settings.defaultNoteSlide && !isNoteSelected) {
          state.selectedNotes = removeNoteByIndex(state.selectedNotes, payload)
          delete clone[interactionId]
          state.liveNotes = clone

          if (Object.keys(clone).length) {
            const [first] = Object.keys(clone)
            state.selectedNote = first
          }
        } else if (!state.settings.defaultNoteSlide || isNoteSelected) {
          clone[interactionId].conf_status = 'completed'
          state.liveNotes = clone
        }
        return
      }

      if (state.liveNotes[payload.interactionId]) {
        const clone = { ...state.liveNotes }
        clone[payload.interactionId] = payload
        state.liveNotes = clone
      } else {
        payload.dateCreated = new Date().getTime()
        state.liveNotes = {
          ...state.liveNotes,
          [payload.interactionId]: payload
        }
      }
    },
    setLiveNoteSessions: (state, { payload }: PayloadAction<LiveNote[]>) => {
      payload.forEach((note) => {
        note.isSession = true
        note.conf_status = 'completed'
        if (state.liveNotes[note.interactionId]) {
          const clone = { ...state.liveNotes }
          clone[note.interactionId] = note
          state.liveNotes = clone
        } else {
          state.liveNotes = {
            ...state.liveNotes,
            [note.interactionId]: note
          }
        }
      })
    },
    updateLiveNotes: (state, { payload }: PayloadAction<LiveNote>) => {
      if (state.liveNotes[payload.conferenceSid]) {
        const clone = { ...state.liveNotes }
        clone[payload.conferenceSid] = payload
        state.liveNotes = clone
      } else {
        state.liveNotes = {
          ...state.liveNotes,
          [payload.conferenceSid]: payload
        }
      }
    },
    setSettings: (state, { payload }: PayloadAction<any>) => {
      const { defaultNoteSlide } = payload
      state.settings = {
        defaultNoteSlide
      }
    }
  }
})

export const {
  setLoading,
  setSelectedCall,
  setSelectedNote,
  setSelectedNotes,
  removeSelectedNotes,
  setCallHistory,
  setCallInfo,
  updateConferences,
  updateUsers,
  setUsers,
  setKazooUsers,
  setAustralisUsers,
  setConferences,
  removeConference,
  removeKazooLiveNote,
  setLiveNoteSessions,
  updateLiveNotes,
  updateKazooLiveNotes,
  setSettings
} = phoneSlice.actions
export default phoneSlice.reducer
export const phoneSelector = (state: { phoneStore: PhoneState }) =>
  state.phoneStore

export const getKazooCallHistory = (
  auth0Token: string,
  accountId: string,
  params = {}
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'history', payload: true }))
    const config = {
      headers: {
        Authorization: `Bearer ${auth0Token}`
      },
      params
    }
    try {
      const kazoo = await HTTP.get(`/kazoo/${accountId}/calls`, config)
      dispatch(setCallHistory(kazoo.data))
      dispatch(setLoading({ type: 'history', payload: false }))
    } catch (error) {
      dispatch(setLoading({ type: 'history', payload: false }))
    }
  }
}

export const getCallHistory = (
  auth0Token: string,
  accountId: string,
  australisId: string,
  params = {}
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'history', payload: true }))
    const conferenceUrl = `/voip/account/${accountId}/user/${australisId}/call`
    const config = {
      headers: {
        Authorization: `Bearer ${auth0Token}`
      },
      params
    }
    try {
      const conference = await HTTP.get(conferenceUrl, config)
      dispatch(setCallHistory(conference.data.calls))
      dispatch(setLoading({ type: 'history', payload: false }))
    } catch (error) {
      dispatch(setLoading({ type: 'history', payload: false }))
    }
  }
}

export const getCallInfo = (
  auth0Token: string,
  accountId: string,
  callId: string,
  interactionId: string,
  call: Call
): AppThunk => {
  return async (dispatch) => {
    dispatch(setCallInfo([]))
    const callInfoUrl = `/kazoo/${accountId}/calls/${callId}/interactions/${interactionId}`
    const config = {
      headers: {
        Authorization: `Bearer ${auth0Token}`
      }
    }
    try {
      const response = await HTTP.get(callInfoUrl, config)
      dispatch(setCallInfo(response.data))
    } catch (error) {
      console.log('error getting call info', error)
    }
  }
}

export const getFellowUsers = (auth0Token: string): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'account', payload: true }))
    try {
      const fullUsers = await HTTP.get('users/fellowUsers', {
        headers: {
          Authorization: `Bearer ${auth0Token}`
        }
      })
      dispatch(setAustralisUsers(fullUsers.data.users))
      dispatch(setLoading({ type: 'account', payload: false }))
    } catch (error) {
      console.log(error)
      dispatch(setLoading({ type: 'account', payload: false }))
    }
  }
}

export const syncKazooData = (
  auth0Token: string,
  accountId: string,
  australisId: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'account', payload: true }))
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${auth0Token}`
        }
      }
      const fullUsers = await HTTP.get('users/fellowUsers', config)
      dispatch(setAustralisUsers(fullUsers.data.users))

      const kazooAccount = await HTTP.get(`/kazoo/${accountId}/account`, config)
      dispatch(setKazooUsers(kazooAccount.data))

      const callSessions = await HTTP.get('/kazoo/sessions', config)
      dispatch(setLiveNoteSessions(callSessions.data))

      const kazooSettings = await HTTP.get(
        `/kazoo/${accountId}/user/${australisId}/settings`,
        config
      )
      dispatch(setSettings(kazooSettings.data))

      dispatch(setLoading({ type: 'account', payload: false }))
    } catch (error) {
      console.log(error)
      dispatch(setLoading({ type: 'account', payload: false }))
    }
  }
}

export const syncVoipData = (
  auth0Token: string,
  accountId: string,
  australisId: string
): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState()
    dispatch(setLoading({ type: 'account', payload: true }))
    const config = {
      headers: {
        Authorization: `Bearer ${auth0Token}`
      }
    }
    try {
      const acc = await HTTP.get(`/voip/account/${accountId}`, config)
      const fullUsers = await HTTP.get('users/fellowUsers', config)

      dispatch(setAustralisUsers(fullUsers.data.users))
      const voipUsers = acc.data.account.australisUsers
      for (const id in voipUsers) {
        const fullUser = fullUsers.data.users.find(
          (fu) => fu.australisId.replaceAll('-', '') === id
        )
        if (fullUser) {
          voipUsers[id] = { ...voipUsers[id], ...fullUser }
        }
      }
      dispatch(setUsers(voipUsers))

      const conf = await HTTP.get(
        `/voip/account/${accountId}/user/${australisId}/conferences`,
        config
      )
      const conferences = JSON.parse(conf.data.body).conferences.filter(
        (c) => c.conf_status !== 'completed' && c.conferenceSid !== 'init'
      )
      dispatch(setConferences(conferences))
      dispatch(setLoading({ type: 'account', payload: false }))
    } catch (error) {
      console.log(error)
      dispatch(setLoading({ type: 'account', payload: false }))
    }

    if (state.phone.australisUsers.length === 0) {
      try {
        const fullUsers = await HTTP.get('users/fellowUsers', config)
        dispatch(setAustralisUsers(fullUsers.data.users))
      } catch (err) {
        console.log(err)
      }
    }
  }
}

export const removeKazooCallSession = (
  auth0Token: string,
  interactionId: string,
  checkSlideSettings?: boolean
): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState()
    if (checkSlideSettings && !state.phone.settings.defaultNoteSlide) {
      const foundNote = { ...state.phone.liveNotes[interactionId] }
      dispatch(setLiveNoteSessions([foundNote]))
      return
    }

    console.log('removing session')
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${auth0Token}`
        }
      }
      const removeSessionUrl = `/kazoo/sessions/interactions/${interactionId}`
      await HTTP.delete(removeSessionUrl, config)
    } catch (error) {
      console.log('removeKazooCallSession', error)
    }
  }
}

export const postKazooLiveNote = (
  auth0Token: string,
  accountId: string,
  australisId: string,
  note: LiveNote,
  payload: KazooLiveNote
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'notes', payload: true }))
    const isCompleted = note.conf_status === 'completed'
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${auth0Token}`
        }
      }

      if (isCompleted) await wait(5000)
      const postKazooUrl = `/communications/${accountId}/user/${australisId}/interaction/${note.interactionId}`
      await HTTP.post(postKazooUrl, payload, config)

      if (!isCompleted) {
        dispatch(
          updateKazooLiveNotes({
            interactionId: note.interactionId,
            ...payload
          })
        )
      } else {
        dispatch(removeSelectedNotes(note.interactionId))
      }
      dispatch(setLoading({ type: 'notes', payload: false }))
    } catch (error) {
      console.log(error)
      dispatch(setLoading({ type: 'notes', payload: false }))
    }
  }
}

export const postLiveNote = (
  auth0Token: string,
  accountId: string,
  australisId: string,
  conferenceName: string,
  payload: LiveNote
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'notes', payload: true }))
    try {
      const res = await HTTP.post(
        `/voip/account/${accountId}/user/${australisId}/conference/${conferenceName}/user`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${auth0Token}`
          }
        }
      )
      dispatch(removeConference(payload.conferenceSid))
      dispatch(setLoading({ type: 'notes', payload: false }))
    } catch (error) {
      console.log(error)
      dispatch(setLoading({ type: 'notes', payload: false }))
    }
  }
}

export const getSettings = (
  auth0Token: string,
  accountId: string,
  australisId: string
): AppThunk => {
  return async (dispatch) => {
    dispatch(setLoading({ type: 'settings', payload: true }))
    try {
      const url = `/kazoo/${accountId}/user/${australisId}/settings`
      const res = await HTTP.get(url, {
        headers: {
          Authorization: `Bearer ${auth0Token}`
        }
      })
      dispatch(setSettings(res.data))
      dispatch(setLoading({ type: 'settings', payload: false }))
    } catch (error) {
      console.log('getSettings', error)
      dispatch(setLoading({ type: 'settings', payload: false }))
    }
  }
}
